import {
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  OnChangeFn,
  SortingState,
  ColumnDef as TanStackColumnDef,
  useReactTable,
} from "@tanstack/react-table";

import React from "react";
import { Row, Table } from "react-bootstrap";
import { filterPopoverStyles } from "Constants/Style";
import DateRangeFilterPopover from "./DateRangeFilterPopover";
import FilterPopover from "./FilterPopover";
import NoDataFound from "../NoDataFound";
import Pagination from "../Pagination/Pagination";
import { MecordColors } from "Constants/Color";

type FilterType = "dropdown" | "text" | "date" | "boolean";

export type CustomColumnDef<TData> = TanStackColumnDef<TData> & {
  filterType?: FilterType;
  filterOptions?: string[];
  filterFetchUrl?: string;
  filterFetchBody?: Record<string, any>;
  filterOptionLabel?: (option: any) => string; // Custom function to transform option label for display

  //*// New changes
  Rnet?: string;
};

interface TableContainerProps<TData = any> {
  // columns: CustomColumnDef<TData>[];
  columns: any;
  data: TData[];
  totalRecords: number;
  pageIndex: number;
  pageSize: number;
  onPageChange: (pageIndex: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  columnFilters?: ColumnFiltersState;
  setColumnFilters?: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
  colValue: any;
  isBordered?: boolean;
  isPagination?: boolean;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  manualSorting: boolean;
  isGlobalFilter?: boolean;
  SearchPlaceholder?: string;
  onTableReady?: (table: any) => void;
}

const MasterTableContainer = ({
  columns,
  data,
  totalRecords,
  pageIndex,
  pageSize,
  onPageChange,
  onPageSizeChange,
  columnFilters,
  setColumnFilters,
  sorting,
  onSortingChange,
  manualSorting = true,
  isBordered = false,
  isPagination = true,
  colValue,
  onTableReady,
}: TableContainerProps) => {
  const handleColumnFilterChange: OnChangeFn<ColumnFiltersState> = (
    updaterOrValue
  ) => {
    if (!setColumnFilters) return;
    setColumnFilters((prev: any) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(prev)
        : updaterOrValue
    );
  };

  // console.log("data: ", data)

  const table = useReactTable({
    columns,
    data,
    manualPagination: true,
    manualFiltering: true,
    manualSorting,
    pageCount: Math.ceil(totalRecords / pageSize),
    state: {
      sorting,
      pagination: {
        pageIndex,
        pageSize,
      },
      columnFilters,
    },
    onSortingChange,
    onColumnFiltersChange: handleColumnFilterChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: !manualSorting ? getSortedRowModel() : undefined,
  });

  React.useEffect(() => {
    if (onTableReady) {
      onTableReady(table);
    }
  }, [table, onTableReady]);

  const { getHeaderGroups, getRowModel, getCanPreviousPage, getCanNextPage } =
    table;

  return (
    <div className="mecord-table-shell">
      {/* Sticky column styles */}
      <style>
        {`
        .sticky-first-column {
          position: sticky;
          left: 0;
          z-index: 11;
          background-color: #fff !important;
        }

        thead .sticky-first-column {
          background-color: ${MecordColors?.LightThemeBGColor} !important;
        }

        .sticky-column {
          position: sticky;
          right: 0;
          z-index: 10;
          background-color: #fff !important;
          border-left: 1px solid #dee2e6;
        }

        thead .sticky-column {
          background-color: ${MecordColors?.LightThemeBGColor} !important;
        }

        .sticky-action {
          box-shadow: -4px 0 6px rgba(0, 0, 0, 0.1);
        }

        .table td,
        .table th {
          background-clip: padding-box;
        }
        .table th {
          border-left:1px solid #ccc
        }
      `}
      </style>

      <div className="table-responsive table-card">
        <Table
          className={`table table-centered align-middle table-custom-effect table-nowrap mb-0 
            }`}
          bordered={isBordered}
        >
          {/* <thead className="table-light"> */}
          <thead style={{ backgroundColor: MecordColors?.LightThemeBGColor }}>
            {getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} >
                {headerGroup.headers.map((header, index) => {
                  const isFirstColumn = index === 0;
                  const isLastColumn = index === headerGroup.headers.length - 1;

                  // Check for stickyRight in meta or fallback to header text check for backwards compatibility
                  const hasStickyRight = (header.column.columnDef.meta as any)?.stickyRight === true;
                  const isActionColumn =
                    header.column.columnDef.header?.toString().toLowerCase() ===
                    "action" ||
                    header.column.columnDef.header?.toString().toLowerCase() ===
                    "actions";

                  const shouldBeStickyRight = hasStickyRight || (isLastColumn && isActionColumn);

                  const isSorted = header.column.getIsSorted();
                  const canSort = header.column.getCanSort();
                  const sortIcon =
                    isSorted === "asc" ? (
                      <i className="ri-arrow-up-line"></i>
                    ) : isSorted === "desc" ? (
                      <i className="ri-arrow-down-line"></i>
                    ) : (
                      <i className="ri-arrow-up-down-line"></i>
                    );

                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={`align-middle ${
                        isFirstColumn ? "sticky-first-column " : ""
                      }${shouldBeStickyRight ? "sticky-column sticky-action " : ""}`}
                    >
                      {!header.isPlaceholder && (
                        <div
                          className="d-flex  align-items-center"
                          style={{ gap: 4 }}
                        >
                          <div
                            className="d-flex align-items-center"
                            style={{
                              gap: 4,
                              cursor: canSort ? "pointer" : "default",
                            }}
                            onClick={
                              canSort
                                ? header.column.getToggleSortingHandler()
                                : undefined
                            }
                          >
                            <span>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </span>
                            {canSort && sortIcon}
                          </div>

                          {header.column.getCanFilter() ? (
                            (header.column.columnDef as CustomColumnDef<any>)
                              .filterType === "date" ? (
                              <DateRangeFilterPopover
                                column={header.column}
                                setColValue={colValue}
                                iconColor={
                                  header.column.getFilterValue()
                                    ? filterPopoverStyles.primaryColor
                                    : "inherit"
                                }
                              />
                            ) : (
                              <FilterPopover
                                column={header.column}
                                columnDef={
                                  header.column
                                    .columnDef as CustomColumnDef<any>
                                }
                                columnFilters={columnFilters}
                                setColumnFilters={setColumnFilters}
                                setColValue={colValue}
                                Rnet={
                                  (
                                    header.column
                                      .columnDef as CustomColumnDef<any>
                                  ).Rnet
                                }
                                iconColor={
                                  header.column.getFilterValue()
                                    ? filterPopoverStyles.primaryColor
                                    : "inherit"
                                }
                              />
                            )
                          ) : null}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.length === 0 ? (
              <NoDataFound colSpan={columns.length} />
            ) : (
              getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell, index) => {
                    const isFirstColumn = index === 0;
                    const isLastColumn =
                      index === row.getVisibleCells().length - 1;

                    // Check for stickyRight in meta or fallback to header text check for backwards compatibility
                    const hasStickyRight = (cell.column.columnDef.meta as any)?.stickyRight === true;
                    const isActionColumn =
                      cell.column.columnDef.header?.toString().toLowerCase() ===
                      "action" ||
                      cell.column.columnDef.header?.toString().toLowerCase() ===
                      "actions";

                    const shouldBeStickyRight = hasStickyRight || (isLastColumn && isActionColumn);

                    return (
                      <td
                        key={cell.id}
                        className={`${
                          isFirstColumn ? "sticky-first-column " : ""
                        }${shouldBeStickyRight ? "sticky-column sticky-action" : ""}`}
                        style={{
                          maxWidth: cell.column.columnDef.maxSize ?? "none",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {isPagination && (
        <Row className="align-items-center mt-2 pt-2 gy-2">
          <Pagination
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </Row>
      )}
    </div>
  );
};

export default MasterTableContainer;
